import * as t from 'io-ts';
import {chain} from 'lodash';

import {makeSafeArrayType} from './types/arrayTypes';
import {makeEnumRuntimeType, makeEnumTypeGuard} from './types/enumTypes';

export enum ChannelTypesEnum {
  // Chat team: AI channel to replace Chat
  COMPANION = 'companion',
  CUSTOM = 'custom',
  // Legacy, we shouldn't have any active channels of that type.
  LAYER_ANON = 'layer_anon',
  FACEBOOK = 'facebook',
  GMAIL = 'gmail',
  GOOGLE_PLAY = 'google_play',
  // Legacy, we shouldn't have any active IMAP channels.
  IMAP = 'imap',
  INTERCOM = 'intercom',
  FORM = 'form',
  OFFICE365 = 'office365',
  SMOOCH = 'smooch',
  SMTP = 'smtp',
  TALKDESK = 'talkdesk',
  // Voice by aircall
  AIRCALL = 'aircall',
  // SMS by aircall
  AIRCALL_SMS = 'aircall_sms',
  // Voice by dialpad
  DIALPAD = 'dialpad',
  // SMS by dialpad
  DIALPAD_SMS = 'dialpad_sms',
  // Voice by RingCentral
  RINGCENTRAL = 'ringcentral',
  // SMS by RingCentral
  RINGCENTRAL_SMS = 'ringcentral_sms',
  SOLUTIONS_BY_TEXT_SMS = 'solutions_by_text_sms',
  // Legacy, we shouldn't have any active channels of that type.
  TRULY = 'truly',
  TWILIO = 'twilio',
  TWILIO_WHATSAPP = 'twilio_whatsapp',
  TWITTER = 'twitter',
  TWITTER_DM = 'twitter_dm',
  YALO_WHATSAPP = 'yalo_wha',
  FRONT_CHAT = 'front_chat',
  FRONT_MAIL = 'front_mail',
  PORTAL = 'portal',
}
export const RChannelType = makeEnumRuntimeType(ChannelTypesEnum);
export const isChannelType = makeEnumTypeGuard(ChannelTypesEnum);

/** List of channel types defined by application channels (as opposed to native channel types) */
export enum ChannelExternalTypesEnum {
  DRIFT = 'drift',
  DIALPAD = 'dialpad_integration',
  DIALPAD_SMS = 'dialpad_sms',
  JUSTCALL = 'justcall',
  YALO = 'yalo',
  LIVEWEB = 'liveweb',
  MEYA = 'meya',
  INSTAGRAM = 'instagram',
  SENDBEE_WHATSAPP = 'sendbee_whatsapp',
  SLACK = 'slack',
  FACEBOOK = 'facebook',
  POSTSCRIPT = 'postscript',
  TELEGRAM = 'telegram',
  WHATSAPP = 'whatsapp',
  UNKNOWN = 'unknown',
}
export const RExternalChannelType = makeEnumRuntimeType(ChannelExternalTypesEnum);
export const isExternalChannelType = makeEnumTypeGuard(ChannelExternalTypesEnum);

export enum ChannelEmailThreadingModesEnum {
  GMAIL = 'gmail',
  OFFICE365 = 'office365',
  REGULAR = 'regular',
  SUBJECT = 'subject',
  OFFICE365_SUBJECT = 'office365_subject',
  SUBJECT_ONLY = 'subject_only',
  DISABLED = 'disabled',
}

export enum ChannelEmailThreadingConfigNamesEnum {
  DEFAULT = 'default',
  DISABLED = 'disabled',
  EXPANDED = 'expanded',
  FRONT = 'front',
  LEGACY = 'legacy',
  PROVIDER = 'provider',
}

export enum ChannelStatusesEnum {
  OK = 'ok',
  QUARANTINED = 'quarantined',
  DEGRADED = 'degraded',
  OFFLINE = 'offline',
  DELETED = 'deleted',
  DEFER_PARTIAL_TRAFFIC = 'defer_partial_traffic',
}
export const RChannelStatus = makeEnumRuntimeType(ChannelStatusesEnum);
export const isChannelStatus = makeEnumTypeGuard(ChannelStatusesEnum);

export interface AutoBcc extends t.TypeOf<typeof RAutoBcc> {}
export const RAutoBcc = t.readonly(
  t.type({
    isEnabled: t.boolean,
    handles: makeSafeArrayType(t.string),
  }),
);

export function parseAutoBcc(src: any): AutoBcc {
  if (!src) {
    return {isEnabled: false, handles: []};
  }

  return {
    isEnabled: Boolean(src.enabled),
    handles: chain(src.val || '')
      .split(/;|,/)
      .map((v) => v.trim())
      .compact()
      .value(),
  };
}

export enum ChannelGroupsEnum {
  EMAIL = 'email',
  CHAT = 'chat',
  SMS = 'sms',
  SLACK = 'slack',

  FALLBACK_UNKNOWN = 'fallback_unknown',
}

export const CHANNEL_TYPES_BY_SELECTED_CHANNEL_GROUP: Record<ChannelGroupsEnum, Array<ChannelTypesEnum>> = {
  [ChannelGroupsEnum.EMAIL]: [ChannelTypesEnum.GMAIL, ChannelTypesEnum.OFFICE365, ChannelTypesEnum.SMTP],
  [ChannelGroupsEnum.CHAT]: [ChannelTypesEnum.FRONT_CHAT],
  [ChannelGroupsEnum.SMS]: [ChannelTypesEnum.TWILIO],
  [ChannelGroupsEnum.SLACK]: [], // Slack channels are identified differently (via applicationChannelTypeUid)
  [ChannelGroupsEnum.FALLBACK_UNKNOWN]: [], // Should not happen, but handle defensively
};

/** Telephony channel types that require billing feature checks */
export const TELEPHONY_BILLING_CHANNEL_TYPES: ReadonlyArray<ChannelTypesEnum> = [
  ChannelTypesEnum.DIALPAD,
  ChannelTypesEnum.DIALPAD_SMS,
  ChannelTypesEnum.AIRCALL,
  ChannelTypesEnum.AIRCALL_SMS,
  ChannelTypesEnum.RINGCENTRAL,
  ChannelTypesEnum.RINGCENTRAL_SMS,
  ChannelTypesEnum.SOLUTIONS_BY_TEXT_SMS,
] as const;
